import type { Product } from '@/types';
import { isLive, showsSoldOut } from '@/lib/catalog/availability';

/**
 * Whether a product may be shown in a store listing at `now` (ms): not archived
 * or draft (the `listProducts` SQL filter) and live (`isLive`: a scheduled
 * drop stays out until its `publishAt`). Sold-out pieces stay listed (owner
 * decision 2026-10-14, replacing A4's "hide them"): their card says «Agotado» /
 * «Pieza vendida» and leads to the PDP with the restock alert. See `listingOrder`.
 */
export function isListed(p: Product, now: number): boolean {
  return p.status !== 'archived' && p.status !== 'draft' && isLive(p, now);
}

/**
 * A store listing ready to show at `now` (ms): `isListed` products, the ones
 * still on sale first and the sold-out ones (`showsSoldOut`) after them, each
 * group keeping the incoming order (stable sort).
 */
export function listingOrder(products: Product[], now: number): Product[] {
  const listed = products.filter((p) => isListed(p, now));
  return [...listed.filter((p) => !showsSoldOut(p)), ...listed.filter(showsSoldOut)];
}

/**
 * The products an Edit article links to, in the article's order, picked from
 * the store listing (already filtered by `isListed`): a missing, archived or
 * draft product is dropped silently instead of breaking the article.
 */
export function pickInOrder(listed: Product[], slugs: string[]): Product[] {
  const bySlug = new Map(listed.map((p) => [p.slug, p]));
  return slugs.flatMap((s) => bySlug.get(s) ?? []);
}
